import { ValidationError } from 'yup';
import qs from 'qs';
import { format } from 'date-fns';
import API_URL from '../apiconfig';
import { CreateArtistValues } from '../utils/form/artistForm';
import { Employee, LoadedUser, PayoutDistributionOptions } from '../interfaces';
import { TypeOnSchedResponse } from '../types/on-sched';
import {
  GetBatchTimeBlocksResponse,
  GetBatchTripsResponse,
  ResourceBlock,
  UpdateEmployeePayload,
  UpdateEmployeeResourcePayload,
} from '../types/employees';
import axios, { isAxiosError } from './api';
import { getLiabilityWaiverText } from '../helpers/employees';
import client from '../utils/react-query/client';

export async function getEmployee(employeeId: string) {
  try {
    const employee = await axios.get(`${API_URL}/v1/employees/${employeeId}`, {
      params: {
        shop: true,
      },
    });

    return employee.data.result;
  } catch (error) {
    throw new Error('Cannot get employee');
  }
}

export async function updateEmployee(
  employeeId: string,
  payload: UpdateEmployeePayload,
) {
  try {
    const employee = await axios.patch(
      `${API_URL}/v1/employees/${employeeId}`,
      payload,
    );

    return employee.data.result;
  } catch (error) {
    if (error instanceof ValidationError) {
      throw new Error(error.message);
    }
    if (isAxiosError(error) && error.response?.status === 409) {
      throw new Error(error.response?.data?.message);
    }
    throw new Error('Cannot update employee');
  }
}

export async function getEmployeeOnschedResource(employeeId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/employees/onsched-resource?employeeId=${employeeId}`,
    );

    return response?.data?.result as TypeOnSchedResponse.Resource;
  } catch (error) {
    throw new Error('Cannot get employee resource');
  }
}

export async function updateEmployeeOnschedResource(
  employeeId: string,
  payload: UpdateEmployeeResourcePayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/employees/weekly-availability?employeeId=${employeeId}`,
      payload.availability,
    );

    return response?.data?.result as unknown;
  } catch (error) {
    throw new Error('Cannot update employee resource');
  }
}

export async function onboardEmployee(
  onboardData: CreateArtistValues,
  currentUser: LoadedUser,
) {
  const liabilityWaiver =
    onboardData.liabilityWaiver ||
    getLiabilityWaiverText(
      onboardData.firstName,
      onboardData.lastName,
      currentUser,
    );

  const payrollType =
    onboardData.payoutDistribution === PayoutDistributionOptions.COMMISSION
      ? 'COMMISSION'
      : 'OTHER';

  const payeeForOtherPayroll =
    onboardData.payoutDistribution === PayoutDistributionOptions.BUSINESS
      ? 'BUSINESS'
      : 'EMPLOYEE';

  try {
    const employee = await axios.post(
      `${API_URL}/v1/employees/onboard`,
      {
        ...onboardData,
        phoneNumber: `+${onboardData.phoneNumber}`,
        liabilityWaiver,
        payrollType,
        payeeForOtherPayroll,
      },
      {
        params: {
          businessId: currentUser.business.id,
        },
      },
    );

    return employee.data.result as Employee;
  } catch (error: any) {
    throw new Error(
      (error.response.data.message as string) || 'Cannot onboard employee',
    );
  }
}

export async function getEmployeesResourceBlocks(
  employeeIds: string[],
  startDate?: Date,
  endDate?: Date,
) {
  const sDate = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
  const eDate = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;

  try {
    const response = await axios.get(
      `${API_URL}/v1/employees/resource-blocks/batch`,
      {
        params: {
          employeeIds: employeeIds.join(','),
          startDate: sDate,
          endDate: eDate,
        },
        paramsSerializer: params =>
          qs.stringify(params, {
            arrayFormat: 'repeat',
          }),
      },
    );

    return response.data.result as GetBatchTimeBlocksResponse;
  } catch (error) {
    throw new Error('Cannot get employee resource blocks');
  }
}

export async function getEmployeesTrips(employeeIds: string[]) {
  try {
    const response = await axios.get(`${API_URL}/v1/trips/with-times/batch`, {
      params: {
        employeeIds: employeeIds.join(','),
      },
      paramsSerializer: params =>
        qs.stringify(params, {
          arrayFormat: 'repeat',
        }),
    });

    return response.data.result as GetBatchTripsResponse;
  } catch (error) {
    throw new Error('Cannot get employee trips');
  }
}

export async function createTimeBlock(
  employeeId: string,
  payload: ResourceBlock,
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/employees/resource-blocks`,
      payload,
      {
        params: {
          employeeId,
        },
      },
    );
    return response.data.result as ResourceBlock;
  } catch (error) {
    throw new Error('Cannot create time block');
  }
}

export async function updateTimeBlock(
  resourceBlockId: number,
  payload: ResourceBlock & { employeeId: string },
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/employees/resource-blocks/${resourceBlockId}`,
      payload,
    );
    return response.data.result as ResourceBlock;
  } catch (error) {
    throw new Error('Cannot update time block');
  }
}

export async function deleteTimeBlock(
  resourceBlockId: number,
  employeeId: string,
) {
  try {
    const response = await axios.delete(
      `${API_URL}/v1/employees/resource-blocks`,
      {
        params: {
          resourceBlockId,
          employeeId,
        },
      },
    );
    return response.data.result as ResourceBlock;
  } catch (error) {
    throw new Error('Cannot delete time block');
  }
}
export async function invalidateBatchTimeBlocks() {
  await client.invalidateQueries({
    queryKey: ['timeBlocksBatch'],
    exact: false,
  });
}

export async function invalidateBatchTimeBlocksEmployee({
  employeeId,
  selectedArtists,
}: {
  employeeId: string;
  selectedArtists: string[];
}) {
  try {
    const sortedEmployeeIds = [...selectedArtists].sort();

    const queryKey = ['timeBlocksBatch', sortedEmployeeIds];

    const isEmployeeSelected = sortedEmployeeIds.includes(employeeId);

    const response = isEmployeeSelected
      ? await getEmployeesResourceBlocks([employeeId])
      : { [employeeId]: [] };

    if (response[employeeId]) {
      await client.setQueryData(
        queryKey,
        (prev: GetBatchTimeBlocksResponse | undefined) => {
          if (!prev) return prev;
          const newData = {
            ...prev,
            ...response,
          };
          return newData;
        },
      );
    }
  } catch {
    // Empty
  }
}
